// One-bit raster for the 640x360 screen (art bible): every pixel is ink or paper, and a lit pixel may carry
// one of three accents (gold, red, plague green). Pure apart from text, which needs a canvas to rasterise.
export const W = 640, H = 360;
export const INK = '#000000', PAPER = '#ffffff';
export const ACCENTS = { gold: '#f2be48', red: '#e24034', green: '#96cd60' };
// The bull and bear knocks' lights (art bible, sprint 9): allowed ONLY behind the glass of a bull or bear knock
// and on its plate and x2 tag, so they are kept apart from the everywhere accents above (like Halloween orange).
export const KNOCK_LIGHTS = { bull: '#38d27e', bear: '#f0464a' };
// The lit knocks' lights (sprint 20, roadmap 44; client/neighbours-lit.js): one per look, the same rules as the bull's light: only
// behind the glass of that look's knock, on its plate, its x2 tag and its step in the sum on the door.
export const LIT_LIGHTS = { pinch: '#e0729c', lintel: '#9484ff' }; // a sick pink, a cold violet
// The bear's looks' lights (sprint 36 item 3; client/neighbours-holden.js), on exactly the bear's terms: only behind the glass of that
// look's knock, on its plate, its x2 tag and its step in the sum on the door. Mr Holden: kraft-paper brown, never red (bear red
// stays the plain bear's); checked against the porch lamp, Halloween orange and gold in test/holden-door.test.js.
export const BEAR_LOOK_LIGHTS = { holden: '#b8946a' };
export const T = { none: 0, gold: 1, red: 2, green: 3, bull: 4, bear: 5, pinch: 6, lintel: 7, holden: 8 };
// tint index -> colour of a lit pixel; an unlit pixel is always ink
export const PALETTE = [PAPER, ACCENTS.gold, ACCENTS.red, ACCENTS.green, KNOCK_LIGHTS.bull, KNOCK_LIGHTS.bear, LIT_LIGHTS.pinch, LIT_LIGHTS.lintel,
  BEAR_LOOK_LIGHTS.holden];
// colours any one-bit frame may show; a bull or bear knock adds its own light (KNOCK_LIGHTS)
export const ALLOWED = [INK, PAPER, ACCENTS.gold, ACCENTS.red, ACCENTS.green];

// Bayer 8x8, values 0..63
export const B8 = (() => {
  let M = [[0]], n = 1;
  while (n < 8) {
    const N = [];
    for (let y = 0; y < n * 2; y++) { N[y] = []; for (let x = 0; x < n * 2; x++) N[y][x] = M[y % n][x % n] * 4 + [[0, 2], [3, 1]][(y / n) | 0][(x / n) | 0]; }
    M = N; n *= 2;
  }
  return M;
})();
export const dz = (x, y, v) => (v * 64 > B8[y & 7][x & 7] + 0.5 ? 1 : 0);

export const FONTS = { big: '20px "Jersey 10"', mid: '15px "Jersey 15"', small: '8px "Tiny5"' };

// measure: optional { measure(s, font) -> width, mask(s, font) -> [x0, y0, x1, y1, ...] } for text
export function createBits(w = W, h = H, glyphs = null) {
  const bits = new Uint8Array(w * h);
  const tint = new Uint8Array(w * h);
  // art: which Aseprite pixel a level 2 night door shows here instead of ink or paper (client/night.js; 0 = none). The one-bit
  // frame never reads it: bits and tint stay exactly what they were.
  const art = new Int32Array(w * h);
  const S = { tint: 0, ox: 0, clip: null, art: 0 };
  const px = (x, y, v) => {
    x = (x | 0) + S.ox; y |= 0;
    if (x < 0 || y < 0 || x >= w || y >= h) return;
    const c = S.clip; if (c && (x < c[0] || y < c[1] || x >= c[0] + c[2] || y >= c[1] + c[3])) return;
    bits[y * w + x] = v; tint[y * w + x] = S.tint; art[y * w + x] = S.art;
  };
  const inv = (x, y) => { x |= 0; y |= 0; if (x >= 0 && y >= 0 && x < w && y < h) bits[y * w + x] ^= 1; };
  const get = (x, y) => (x >= 0 && y >= 0 && x < w && y < h ? bits[y * w + x] : 0);
  const fill = (x, y, ww, hh, v) => { for (let j = y; j < y + hh; j++) for (let i = x; i < x + ww; i++) px(i, j, typeof v === 'function' ? v(i, j) : v); };
  const hline = (x, y, ww, v = 0) => fill(x, y, ww, 1, v), vline = (x, y, hh, v = 0) => fill(x, y, 1, hh, v);
  const box = (x, y, ww, hh, v = 0) => { hline(x, y, ww, v); hline(x, y + hh - 1, ww, v); vline(x, y, hh, v); vline(x + ww - 1, y, hh, v); };
  const dotted = (x, y, ww, v = 0) => { for (let i = 0; i < ww; i += 2) px(x + i, y, v); };
  const xbox = (x, y, ww, hh) => {
    x |= 0; y |= 0; ww |= 0; hh |= 0; if (ww < 2 || hh < 2) return;
    for (let i = x; i < x + ww; i++) { inv(i, y); inv(i, y + hh - 1); }
    for (let j = y + 1; j < y + hh - 1; j++) { inv(x, j); inv(x + ww - 1, j); }
  };
  const xfill = (x, y, ww, hh) => { for (let j = y; j < y + hh; j++) for (let i = x; i < x + ww; i++) inv(i, j); };
  const tw = (s, f) => (glyphs ? glyphs.measure(s, f) : s.length * 6);
  // pattern: optional (x, y) -> bool; only pixels where it is true are set (dithered "disabled" text)
  function text(s, x, y, f, v = 0, align = 'left', pattern = null) {
    if (!glyphs || !s) return tw(s ?? '', f);
    const ww = tw(s, f), pts = glyphs.mask(s, f);
    const ox = Math.round(align === 'right' ? x - ww : align === 'center' ? x - ww / 2 : x);
    for (let i = 0; i < pts.length; i += 2) { const X = ox + pts[i], Y = y + pts[i + 1]; if (!pattern || pattern(X, Y)) px(X, Y, v); }
    return ww;
  }
  function wrap(s, f, max) {
    const out = []; let line = '';
    for (const word of String(s).split(/\s+/).filter(Boolean)) {
      const t = line ? line + ' ' + word : word;
      if (tw(t, f) > max && line) { out.push(line); line = word; } else line = t;
    }
    if (line) out.push(line);
    return out;
  }
  // write the frame as 32-bit ABGR pixels (canvas ImageData order on little-endian machines)
  const ABGR = PALETTE.map(c => (0xff000000 | (parseInt(c.slice(5, 7), 16) << 16) | (parseInt(c.slice(3, 5), 16) << 8) | parseInt(c.slice(1, 3), 16)) >>> 0);
  function blit(d32) { for (let i = 0; i < w * h; i++) d32[i] = bits[i] ? ABGR[tint[i]] : 0xff000000; }
  return { w, h, bits, tint, art, S, px, inv, get, fill, hline, vline, box, dotted, xbox, xfill, tw, text, wrap, blit };
}

// Text raster for the browser: draw with the pixel font, threshold to one bit, cache.
export function canvasGlyphs(doc = document) {
  const c = doc.createElement('canvas'); c.width = 900; c.height = 48;
  const g = c.getContext('2d', { willReadFrequently: true });
  const wc = new Map(), mc = new Map();
  // glyphs drawn before a pixel font arrived used a fallback font: forget them once fonts finish loading
  doc.fonts?.addEventListener?.('loadingdone', () => { wc.clear(); mc.clear(); });
  const measure = (s, f) => { const k = f + '\u0000' + s; if (!wc.has(k)) { g.font = FONTS[f]; wc.set(k, Math.ceil(g.measureText(s).width)); } return wc.get(k); };
  const mask = (s, f) => {
    const k = f + '\u0000' + s; if (mc.has(k)) return mc.get(k);
    const ww = Math.min(c.width - 8, measure(s, f) + 4);
    g.fillStyle = '#fff'; g.fillRect(0, 0, c.width, c.height);
    g.font = FONTS[f]; g.textBaseline = 'top'; g.fillStyle = '#000'; g.fillText(s, 2, 2);
    const d = g.getImageData(0, 0, ww + 4, 32).data, pts = [];
    for (let j = 0; j < 32; j++) for (let i = 0; i < ww + 4; i++) if (d[(j * (ww + 4) + i) * 4] < 128) pts.push(i - 2, j - 2);
    if (mc.size > 4000) mc.clear();
    mc.set(k, pts);
    return pts;
  };
  return { measure, mask };
}
